import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import HiddenBlockView from '../components/chapter/HiddenBlockView/HiddenBlockView';

// HiddenBlock — block-level container whose body is only delivered to roles
// listed in `visible` (server-side `snippets.StripForRole` enforces this). The
// markdown wire form is:
//   [[hidden id=<uuid> visible=editor,viewer]]
//   ...body markdown...
//   [[/hidden]]
//
// The React NodeView exposes per-snippet visibility toggles so owner/admin
// can grant or revoke editor/viewer access inline. Wrapper paints magenta
// when private, green when shared with any lower role.
export const HiddenBlock = Node.create({
  name: 'hiddenBlock',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      id: { default: null },
      visible: {
        default: [],
        parseHTML: (el) => (el.getAttribute('data-visible') || '').split(',').filter(Boolean),
        renderHTML: (attrs) => ({ 'data-visible': (attrs.visible || []).join(',') }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-hidden-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-hidden-block': '',
        class: 'hidden-block',
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HiddenBlockView);
  },
});

// HiddenPlaceholder — opaque marker rendered to clients that lack visibility
// for a hidden block. Atom (no editable content). Stable id so the server can
// reconcile the original body back on the round-trip.
//   [[hidden id=<uuid> redacted]]
export const HiddenPlaceholder = Node.create({
  name: 'hiddenPlaceholder',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return { id: { default: null } };
  },

  parseHTML() {
    return [{ tag: 'div[data-hidden-placeholder]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-hidden-placeholder': '',
        class: 'hidden-placeholder',
        contenteditable: 'false',
        title: 'Hidden block — delete to permanently lose its body',
      }),
      '[ hidden — owner/admin only ]',
    ];
  },
});
